import { useQueryClient } from '@tanstack/react-query';
import { logError } from 'components/LogError';
import { AppContext } from 'context/ContextProvider';
import { useContext, useMemo, useState } from 'react';
import { PORTAL_DESCRIPTIONS } from 'types/commonTypes';
import { useHandleAcceptBid } from 'views/shared/hooks/useHandleAcceptBid';
import { STOP_LIMIT_TYPE } from 'views/Trade/constants';
import { useFetch } from 'views/Trade/hooks/useFetch';
import useMutate from 'views/Trade/hooks/useMutate';
import { IAcceptHighestBidBody } from 'views/Trade/services/acceptHighestBid';
import { deleteOffer, IDeleteOffersParams } from 'views/Trade/services/deleteOffer';
import { fetchPortfolioOffers } from 'views/Trade/services/fetchPortfolioOffers';
import { fetchPortfolioStocks } from 'views/Trade/services/fetchPortfolioStocks';
import { PortfolioOffersType, QUERY_KEYS } from 'views/Trade/types';

const PAGE = 0;
const PAGESIZE = 10;

enum ACCEPT_BID_STATUS {
  success = 'success',
  error = 'error',
  isLoading = 'isLoading',
}

const useAcceptBid = (lwin18: string, highestBidPrice: number | null) => {
  const queryClient = useQueryClient();
  const {
    state: {
      settings: { currency },
    },
  } = useContext(AppContext);

  const { handleAcceptBid } = useHandleAcceptBid();
  const { mutateAsync: mutateDeleteOffer } = useMutate({ mutationFn: deleteOffer });
  const [isProccessing, setIsProccessing] = useState(false);
  const [status, setStatus] = useState<ACCEPT_BID_STATUS | null>(null);

  const getOffers = async () => {
    try {
      const offers = await fetchPortfolioOffers({ search: lwin18, page: PAGE, pageSize: PAGESIZE });
      if (!offers?.count) return null;
      // The endpoint runs a fuzzy `queryStr` search, so keep only this exact wine - otherwise we would
      // read the account from, and delete the outstanding offers of, an unrelated holding.
      const matchingOffers = offers.data.filter((offer) => offer.lwin18 === lwin18);
      return matchingOffers.length ? matchingOffers : null;
    } catch (error) {
      logError(JSON.stringify({ error, lwin18, highestBidPrice }), { tags: { userFlow: 'acceptBid' } });
      return null;
    }
  };

  const getStocks = async () => {
    try {
      const stocks = await fetchPortfolioStocks({ search: lwin18, page: PAGE, pageSize: PAGESIZE });
      if (!stocks?.count) return null;
      return stocks.data.find((stock) => stock.lwin18 === lwin18) ?? null;
    } catch (error) {
      logError(JSON.stringify({ error, lwin18, highestBidPrice }), { tags: { userFlow: 'acceptBid' } });
      return null;
    }
  };

  const deleteExistingOffers = async (offersData: PortfolioOffersType[] | null, quantity: number) => {
    if (!offersData?.length) return null;
    offersData.sort((a, b) => a.quantityOutstanding - b.quantityOutstanding);

    let targetedQty = quantity;
    /* eslint-disable no-await-in-loop */
    for (let i = 0; i < offersData.length; i++) {
      const { quantityOutstanding, id } = offersData[i];

      if (quantityOutstanding && targetedQty > 0) {
        targetedQty -= quantityOutstanding;
        const deleteOfferParams: IDeleteOffersParams = {
          headers: {
            emailAddress: 'portal',
            operationType: 'DeleteSellOrder',
          },
          orderId: id,
        };

        await mutateDeleteOffer(deleteOfferParams);
      }
    }
    return new Promise((resolve) => {
      // delay
      setTimeout(() => {
        resolve('done');
      }, 1500);
    });
  };

  const acceptBid = async (selectedQty: number) => {
    if (!lwin18) {
      setStatus(ACCEPT_BID_STATUS.error);
      logError(JSON.stringify({ error: 'missing lwin18', lwin18, highestBidPrice }), {
        tags: { userFlow: 'acceptBid' },
      });
      throw new Error('accept bid: missing lwin18');
    }

    setIsProccessing(true);

    const offers = await getOffers();
    const stocks = await getStocks();

    // Not enough (or no) stock on hand, so the outstanding offers are freed up to cover the bid.
    const sellFromOffers = !stocks || selectedQty > stocks.quantity;
    const accountId = (sellFromOffers ? offers?.[0]?.assetAccountId : stocks?.assetAccountId) || '';

    // Bail before deleting anything - a request without an account is rejected downstream, and
    // cancelling the existing offers first would leave the client with neither those nor a new order.
    if (!accountId) {
      setIsProccessing(false);
      setStatus(ACCEPT_BID_STATUS.error);
      logError(JSON.stringify({ error: 'missing assetAccountId', lwin18, highestBidPrice, selectedQty }), {
        tags: { userFlow: 'acceptBid' },
      });
      throw new Error('accept bid: missing accountId');
    }

    if (sellFromOffers) {
      await deleteExistingOffers(offers, selectedQty);
    }

    const requestBody: IAcceptHighestBidBody = {
      accountId,
      description: PORTAL_DESCRIPTIONS.PORTAL_ACCEPTBID_PORTFOLIO,
      items: [
        {
          lwin18,
          stopLimitPerUnit: highestBidPrice as number,
          stopLimitType: STOP_LIMIT_TYPE.customPrice,
          priceType: 'AcceptHighestBid',
          quantity: selectedQty,
        },
      ],
    };

    // console.log('requestBody  ', requestBody);

    await new Promise((resolve, reject) => {
      handleAcceptBid(requestBody, {
        success: () => {
          setStatus(ACCEPT_BID_STATUS.success);
          setTimeout(() => {
            queryClient.resetQueries({ queryKey: ['fetchCurrentHoldingVT2', { currency }] });
            queryClient.refetchQueries({ queryKey: ['fetchMyCellar'] });
          }, 1200);
          resolve('done');
        },
        isLoading: () => {},
        error: () => {
          setStatus(ACCEPT_BID_STATUS.error);
          reject(new Error('accept bid error'));
        },
        finally: () => {
          setIsProccessing(false);
        },
      });
    });
  };

  return { isProccessing, status, acceptBid };
};

export default useAcceptBid;
